import { usePageTitle } from '../hooks/usePageTitle';
import { useState } from 'react';

const DicePage = () => {
  usePageTitle('Dice | Quinexa');

  // --- CREDITS INITIALIZATION ---
  const getInitialCredits = () => {
    const storedCredits = localStorage.getItem('QuinexaCredits');
    if (storedCredits === null) {
      localStorage.setItem('QuinexaCredits', '1000');
      return 1000;
    }
    return parseInt(storedCredits, 10);
  };

  // --- STATES ---
  const [credits, setCredits] = useState(getInitialCredits());
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('currentUser')) || { name: 'Guest' });
  const [bet, setBet] = useState(50);
  const [prediction, setPrediction] = useState('high');
  const [diceValues, setDiceValues] = useState({ d1: '?', d2: '?' });
  const [isPlaying, setIsPlaying] = useState(false);
  const [result, setResult] = useState(null); 
  const [history, setHistory] = useState(JSON.parse(localStorage.getItem('gameHistory')) || []);

  // --- LOGIC ---
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleRoll = async () => {
    if (bet < 10) return alert('Minimum bet is 10 credits');
    if (credits < bet) return alert('Not enough credits');

    setIsPlaying(true);
    setResult(null);

    const currentCredits = credits - bet;
    updateGlobalCredits(currentCredits);

    for (let i = 0; i < 12; i++) {
      setDiceValues({
        d1: Math.floor(Math.random() * 6) + 1,
        d2: Math.floor(Math.random() * 6) + 1
      });
      await sleep(100);
    }

    const val1 = Math.floor(Math.random() * 6) + 1;
    const val2 = Math.floor(Math.random() * 6) + 1;
    const total = val1 + val2;
    setDiceValues({ d1: val1, d2: val2 });

    let won = false;
    let multiplier = 1;

    if (prediction === 'high' && total >= 8) {
      won = true;
      multiplier = 2;
    } else if (prediction === 'low' && total <= 6) {
      won = true;
      multiplier = 2;
    } else if (prediction === 'seven' && total === 7) {
      won = true;
      multiplier = 5;
    } else if (prediction === 'doubles' && val1 === val2) {
      won = true;
      multiplier = 6;
    }

    const winAmount = won ? bet * multiplier : 0;
    const finalCredits = currentCredits + winAmount;

    setResult({ won, amount: won ? winAmount : bet, total });
    
    updateGlobalCredits(finalCredits);
    addHistory('Dice', bet, total, won);
    
    setIsPlaying(false);
  };

  const updateGlobalCredits = (newAmount) => {
    setCredits(newAmount);
    localStorage.setItem('QuinexaCredits', newAmount.toString());

    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser) {
      currentUser.credits = newAmount;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      setUser(currentUser); 

      const allUsers = JSON.parse(localStorage.getItem('users')) || [];
      const updatedUsers = allUsers.map(u => u.email === currentUser.email ? { ...u, credits: newAmount } : u);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
    }
  };

  const addHistory = (game, bet, res, won) => {
    const newEntry = { id: Date.now(), game, bet, result: res, won, date: new Date().toLocaleTimeString() };
    const updatedHistory = [newEntry, ...history].slice(0, 5);
    setHistory(updatedHistory);
    localStorage.setItem('gameHistory', JSON.stringify(updatedHistory));
  };

  return (
    <main className="main">
      <div className="container">
        <section className="game-page">
          <div className="game-container">
            <div className="game-main">
              <div className="game-header">
                <div className="game-header-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <circle cx="8" cy="8" r="1.5" fill="white" />
                    <circle cx="16" cy="8" r="1.5" fill="white" />
                    <circle cx="12" cy="12" r="1.5" fill="white" />
                    <circle cx="8" cy="16" r="1.5" fill="white" />
                    <circle cx="16" cy="16" r="1.5" fill="white" />
                  </svg>
                </div>
                <h1 className="game-header-title">Dice</h1>
              </div>

              <div className="game-area">
                <div className="dice-container">
                  <div className={`dice ${isPlaying ? 'rolling' : ''}`}>{diceValues.d1}</div>
                  <div className={`dice ${isPlaying ? 'rolling' : ''}`}>{diceValues.d2}</div>
                </div>

                <div className="game-controls">
                  <div className="bet-input">
                    <label>Bet:</label>
                    <input 
                      type="number" 
                      value={bet} 
                      onChange={(e) => setBet(parseInt(e.target.value) || 0)}
                      disabled={isPlaying}
                      min="10" 
                    />
                  </div>

                  <div className="bet-input">
                    <label>Predict:</label>
                    <select 
                      value={prediction} 
                      onChange={(e) => setPrediction(e.target.value)}
                      disabled={isPlaying}
                      className="dice-select"
                    >
                      <option value="high">High (8-12)</option>
                      <option value="low">Low (2-6)</option>
                      <option value="seven">Lucky 7</option>
                      <option value="doubles">Doubles</option>
                    </select>
                  </div>

                  <button 
                    className="btn btn-primary" 
                    onClick={handleRoll}
                    disabled={isPlaying}
                  >
                    {isPlaying ? 'Rolling...' : 'Roll Dice'}
                  </button>
                </div>

                <div className="game-result">
                  {result && (
                    <>
                      <div className="result-label">
                        Total: {result.total} {result.won ? '- YOU WON!' : ''}
                      </div>
                      <div className={`result-value ${result.won ? 'win' : 'lose'}`}>
                        {result.won ? `+${result.amount}` : `-${result.amount}`}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="panel mt-20">
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-medium)' }}>
                  <strong>How to play:</strong> Choose your prediction, set your bet, and roll the dice.
                  Win multipliers: High/Low = 2x | Lucky 7 = 5x | Doubles = 6x
                </p>
              </div>
            </div>

            <div className="game-sidebar">
              <div className="sidebar-card">
                <h3>Your Credits</h3>
                <div className="credits-display">{credits.toLocaleString()}</div>
              </div>

              <div className="sidebar-card">
                <h3>Recent Plays</h3>
                <div className="history-list">
                  {history.length === 0 ? (
                    <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No plays yet</p>
                  ) : (
                    history.map(item => (
                      <div key={item.id} className="history-item">
                        <span>{item.date}</span> | <span>Sum: {item.result}</span> | 
                        <span className={item.won ? 'text-win' : 'text-lose'}> {item.won ? 'Win' : 'Loss'}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="sidebar-card">
                <h3>Win Multipliers</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-medium)', lineHeight: '1.8' }}>
                  High (8-12): <strong style={{ color: 'var(--primary)' }}>2x</strong><br />
                  Low (2-6): <strong style={{ color: 'var(--primary)' }}>2x</strong><br />
                  Lucky 7: <strong style={{ color: 'var(--primary)' }}>5x</strong><br />
                  Doubles: <strong style={{ color: 'var(--primary)' }}>6x</strong>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default DicePage;